import React, { useState, useEffect, useCallback } from "react";
import "./D04_Responses_by_Demographic_Category.css";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import StackedBarChart from "../components/Dashboardviz/D04/DB04Viz1";
import MultipleBarCharts from "../components/Dashboardviz/D04/DB04Viz2";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import Select from "react-select";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-numeric.json";

const D04_Responses_by_Demographic_CategoryDashboard = () => {
  const { filterCriteria } = useFilter();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState("MQ0026");
  const [selectedDemographic, setSelectedDemographic] = useState(
    "demographic_category_05"
  );
  const [expandedCards, setExpandedCards] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  // Add a loading state to prevent rendering errors during data transitions
  const [isLoading, setIsLoading] = useState(false);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const toggleFilterSidebar = useCallback(() => {
    setIsFilterSidebarOpen((prev) => !prev);
  }, []);

  // Add refresh page functionality
  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    const sortAlphabetically = (array) =>
      array.filter(Boolean).sort((a, b) => a.localeCompare(b));

    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter((value) => value !== null);

        if (sortOrder && Array.isArray(sortOrder) && sortOrder.length > 0) {
          // Only use sortOrder if it's a valid array
          uniqueValues = uniqueValues.sort((a, b) => {
            // Check if both values exist in sortOrder
            const indexA = sortOrder.indexOf(a);
            const indexB = sortOrder.indexOf(b);
            
            // If both exist in sortOrder, use the sortOrder indices
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // If only one exists, prioritize the one in sortOrder
            else if (indexA !== -1) {
              return -1;
            }
            else if (indexB !== -1) {
              return 1;
            }
            // If neither exists in sortOrder, fall back to alphabetical
            else {
              return a.localeCompare(b);
            }
          });
        } else {
          uniqueValues = sortAlphabetically(uniqueValues);
        }

        options[fieldName] = uniqueValues;
      });

      return options;
    };

    setFilterOptions(generateFilterOptions());
  }, []);

  const handleDemographicChange = (selectedOption) => {
    if (!selectedOption) return; // Guard against null option
    
    // Set loading state to prevent rendering errors during transition
    setIsLoading(true);
    
    // Use a timeout to allow state updates to complete before rendering
    setTimeout(() => {
      setSelectedDemographic(selectedOption.value);
      setIsLoading(false);
    }, 50);
  };

  const handleQuestionChange = (selectedOption) => {
    if (!selectedOption) return; // Guard against null option
    
    // Set loading state to prevent rendering errors during transition
    setIsLoading(true);
    
    // Use a timeout to allow state updates to complete before rendering
    setTimeout(() => {
      setSelectedQuestion(selectedOption.value);
      setIsLoading(false);
    }, 50);
  };

  const toggleCardExpansion = (index) => {
    setExpandedCards((prevState) =>
      prevState.includes(index)
        ? prevState.filter((i) => i !== index)
        : [...prevState, index]
    );
  };

  // Ensure demographicOptions is properly initialized with valid values
  const demographicOptions = React.useMemo(() => {
    return FILTER_CONFIG
      .filter(item => item && item.fieldName && item.displayName) // Guard against malformed config
      .map(({ fieldName, displayName }) => ({
        value: fieldName,
        label: displayName,
      }));
  }, []);

  const questionOptions = [
    { value: "MQ0044", label: "My job responsibilities are clear to me." },
    {
      value: "MQ0047",
      label: "I am given realistic and achievable goals and objectives.",
    },
    /* remaining options omitted for brevity */
  ];

  return (
    <div className="D04-dashboard-container">
      <Header
        title="Responses by Demographic Category"
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshPage}
      />
      <NavigationSidebar
        isOpen={isSidebarOpen}
        className={
          isSidebarOpen
            ? "navigation-sidebar-open"
            : "navigation-sidebar-closed"
        }
      />
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        filterOptions={filterOptions}
      />
      <div className="D04-main-content">
        <div className="D04-dropdowns">
          <div className="D04-dropdown">
            <h2>Select Demographic Category</h2>
            <Select
              options={demographicOptions}
              value={demographicOptions.find(
                (option) => option.value === selectedDemographic
              )}
              onChange={handleDemographicChange}
            />
          </div>
          <div className="D04-dropdown">
            <h2>Select Question</h2>
            <Select
              options={questionOptions}
              value={questionOptions.find(
                (option) => option.value === selectedQuestion
              )}
              onChange={handleQuestionChange}
            />
          </div>
        </div>
        <div className="D04-grid-container">
          {!isLoading && (
            <>
              <div
                className={`D04-card ${
                  expandedCards.includes(0) ? "expanded" : ""
                }`}
                onClick={() => toggleCardExpansion(0)}
              >
                <h2 className="D04-card-header">
                  Percentages within each Demographic Category
                </h2>
                <div className="D04-card-content">
                  <StackedBarChart
                    selectedQuestion={selectedQuestion}
                    selectedDemographic={selectedDemographic}
                    filterCriteria={filterCriteria}
                  />
                </div>
              </div>
              <div
                className={`D04-card ${
                  expandedCards.includes(1) ? "expanded" : ""
                }`}
                onClick={() => toggleCardExpansion(1)}
              >
                <h2 className="D04-card-header">
                  Percentages within each Favorability Group
                </h2>
                <div className="D04-card-content">
                  <MultipleBarCharts
                    selectedQuestion={selectedQuestion}
                    selectedDemographic={selectedDemographic}
                    filterCriteria={filterCriteria}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default D04_Responses_by_Demographic_CategoryDashboard;